<template>
  <div>
    <div class="history-header">
      <div class="history-heading">History</div>
      <button @click="close" class="btn btn-primary">Close</button>
    </div>
    <div class="history-container" v-if="historyRecords.length">
      <div class="history-diff">
        <!-- Box for Additions and Deletions, placed above the JSON view -->
        <div class="change-summary">
          <div class="change-summary-text">
            <div v-if="additionsCount || deletionsCount">
              There are <span class="additions">{{ additionsCount }} additions</span> and <span class="deletions">{{ deletionsCount }} deleted rows</span> in this file
            </div>
            <div v-else>
              There are no changes in this file
            </div>
          </div>
          <div v-if="totalChanges" class="change-navigation">
            <button @click="scrollToPreviousChange" class="btn fixed-width"> &lt; </button>
            <span>Showing change {{ currentChangeIndex + 1 }} of {{ totalChanges }}</span>
            <button @click="scrollToNextChange" class="btn fixed-width"> &gt; </button>
          </div>
        </div>
        <div v-if="showDiff">
          <vue-json-compare
            :newData="JSON.parse(newData)"
            :oldData="JSON.parse(oldData)"
          />
        </div>
      </div>
      <div class="history-nav">
        <div class="history-nav-content">
          <div class="history-nav-items">
            <div v-for="(historyRecord, index) in historyRecords" :key="index" class="history-nav-item">
              <div class="history-record" @click="toggle(historyRecord, index)" :class="{active: isSelected(index)}">
                <div>
                  <div>Published by {{ historyRecord.author }}</div>
                  <div>Created on {{ historyRecord.createdOn | date('dd-MM-yyyy HH:mm:ss') }}</div>
                </div>
                <div v-if="isSelected(index)">
                  <button @click="restore(historyRecord)" class="btn">Restore</button>
                </div>
              </div>
            </div>
          </div>
          <button v-if="canShowMoreHistoryRecords" @click="showMore" class="btn btn-primary pull-right history-more">
            Show more
          </button>
        </div>
      </div>
    </div>
  </div>
</template>


<script>
import { mapGetters } from 'vuex'
import VueJsonCompare from 'vue-json-compare'

export default {
  data() {
    return {
      newData: '',
      oldData: '',
      selectedIndex: null,
      currentChangeIndex: 0,
      showChangeSummary: false,
      additionsCount: 0,
      deletionsCount: 0,
      changes: []
    }
  },
  computed: {
    ...mapGetters(['historyRecords', 'canShowMoreHistoryRecords', 'dataRestored']),
    showDiff() {
      return this.newData !== ''
    },
    totalChanges() {
      return this.changes.length;
    }
  },
  created() {
    this.$store.dispatch('getEditorData').then(() => {
      this.$store.dispatch('getHistoryRecords')
    })
  },
  beforeDestroy() {
    this.$store.commit('clearHistoryRecords')
  },
  methods: {
    showMore() {
      this.$store.dispatch('getHistoryRecords', true)
    },
    getPreviousHistory(index) {
      return index === this.historyRecords.length - 1 ? this.historyRecords[index] : this.historyRecords[index + 1]
    },
    toggle(historyRecord, index) {
      const previousHistoryRecord = this.getPreviousHistory(index)
      this.selectedIndex = index
      this.newData = historyRecord.data
      this.oldData = previousHistoryRecord.data
      this.resetState() // Reset counts and index before updating
      this.$nextTick(() => {
        this.updateChangeSummary();
        this.scrollToFirstChange(); // Automatically scroll to the first change
      });
    },
    updateChangeSummary() {
      this.changes = this.getChanges();
      this.additionsCount = this.changes.filter(change => change.classList.contains('c-line-add')).length;
      this.deletionsCount = this.changes.filter(change => change.classList.contains('c-line-del')).length;
      this.showChangeSummary = this.additionsCount > 0 || this.deletionsCount > 0;
    },
    getChanges() {
      return [...document.querySelectorAll('.c-line-add, .c-line-del')];
    },
    scrollToFirstChange() {
      if (this.changes.length > 0) {
        this.currentChangeIndex = 0;
        this.changes[this.currentChangeIndex].scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    },
    restore(historyRecord) {
      this.$store.commit('setDataToRestore', historyRecord.data)
      this.$modal.show('restore-data-modal')
    },
    isSelected(index) {
      return this.selectedIndex === index
    },
    close() {
      this.$router.push({ name: 'ValueEditor' })
    },
    scrollToNextChange() {
      if (this.changes.length > 0) {
        this.currentChangeIndex = (this.currentChangeIndex + 1) % this.changes.length;
        this.changes[this.currentChangeIndex].scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    },
    scrollToPreviousChange() {
      if (this.changes.length > 0) {
        this.currentChangeIndex = (this.currentChangeIndex - 1 + this.changes.length) % this.changes.length;
        this.changes[this.currentChangeIndex].scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    },
    resetState() {
      this.currentChangeIndex = 0
      this.showChangeSummary = false;
      this.additionsCount = 0
      this.deletionsCount = 0
      this.changes = []
    }
  },
  watch: {
    dataRestored(val) {
      if (val) {
        this.resetState()
        this.$store.dispatch('getHistoryRecords')
        this.$store.commit('clearDataRestored')
      }
    }
  },
  components: {
    VueJsonCompare
  }
}
</script>

<style scoped>
.change-summary {
  background-color: #f8f9fa;
  padding: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #ddd;
  min-height: 50px;
}

.change-summary-text {
  width: 50%;
}

.additions {
  background-color: #e6ffed;
  padding: 2px 4px;
  border-radius: 4px;
}

.deletions {
  background-color: #ffeef0;
  padding: 2px 4px;
  border-radius: 4px;
}

.change-navigation {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 50%;
  justify-content: flex-end;
}

.btn.fixed-width {
  width: 40px;
  text-align: center;
}

.history-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}
@media (max-width: 768px) {
  .history-header {
    margin-left: 10px;
    margin-right: 10px;
  }
}
.history-heading {
  font-size: 20px;
}
.history-container {
  display: flex;
}
@media (max-width: 768px) {
  .history-container {
    flex-direction: column;
  }
}
.history-diff {
  flex: 0 0 70%;
  padding-right: 10px;
}
.history-nav {
  flex: 0 0 30%;
  padding-left: 10px;
}
@media (max-width: 768px) {
  .history-diff {
    padding-left: 10px;
  }
  .history-nav {
    padding-right: 10px;
    margin-top: 20px;
  }
}
.history-nav-content {
  background-color: #ddd;
  overflow-y: auto;
  height: calc(100vh - 190px);
}
.history-nav-items {
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 20px;
}
.history-nav-item {
  margin-bottom: 10px;
  border-radius: 3px;
  background-color: #f5f5f5;
}
.history-nav-item:last-child {
  margin-bottom: 0;
}
.history-record {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  cursor: pointer;
}
.history-record:hover,
.history-record.active {
  background-color: #337ab7;
}
.history-more {
  margin-right: 20px;
  margin-bottom: 20px;
}
.c-json-view {
  overflow-y: auto;
  height: calc(100vh - 190px);
  margin-bottom: 0;
}
</style>



// WEBPACK FOOTER //
// src/components/History.vue